import "./SpendAndEffectModal.scss";
import React, { useState, useMemo, useCallback, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { Sankey } from "recharts";
import cn from "classnames";

// Components
import ChartContainer from "../ChartContainer";
import WidgetContainer from "../WidgetContainer";
import { Dropdown, DropdownToggleType } from "../Dropdown";
import { DownloadDropdown } from "../DownloadDropdown";
import { ToggleSwitch, ToggleSwitchType } from "../ToggleSwitch";
import { VisualLegendHorizontal } from "../VisualLegendHorizontal";
import { ShadedPattern } from "../../MMM/FillPatterns/ShadedPattern";
import { SpendAndEffectBarChart } from "../../MMM/ModelResults/SpendAndEffectBarChart";
import { Button, ButtonType } from "../Button";
import { ButtonFrameworkVariant } from "../ButtonFramework";
import { OverlayTrigger } from "../OverlayTrigger";
import { TiArrowBack } from "react-icons/ti";
import { MdOutlineMenuBook, MdOutlineWarningAmber } from "react-icons/md";
import { TooltipOverlay } from "../TooltipOverlay/TooltipOverlay";
import {
  SPEND_AND_EFFECT_GLOSSARY_DEFINITIONS,
  SpendAndEffectGlossary,
} from "./SpendAndEffectGlossary";

// Utils
import { Brand60, colorMap as colors, Neutral300, Neutral400 } from "../../utils/colors";
import {
  spendAndEffectDataSort,
  getBarChartContributorsData,
  currencyFormatter,
  numberFormatter,
} from "../../MMM/MMMUtils";
import { formatMoney } from "../../utils/format-utils";
import { lessThanIgnoreTime } from "@blisspointmedia/bpm-types/dist/FiscalCalendar";

interface SpendAndEffectModalProps {
  onClose: () => void;
  data: any[];
  valueFormatter: (value: number) => string;
  sortOptions: { value: string }[];
  kpiType: string;
  excelDownload: () => void;
  groupBy: string;
  setGroupBy: (groupBy: string) => void;
  groupByOptions: { label: string; value: string }[];
  sortDropdownValue: string;
  setSortDropdownValue: (value: string) => void;
  modelDate: string;
}

const PRIMARY_SANKEY_SET = new Set([
  "activeChannelEffect2",
  "activeChannelEffect",
  "activeChannelSpend",
  "activeChannelSpend2",
]);

const ALL_CHANNELS = "All";
const DEMAND_GEN = "Demand Generation";
const DEMAND_CAP = "Demand Capture";
export const DEMAND_GEN_VS_CAP = "Demand Gen vs Capture";
export const SINGLE_COLOR_SORT_OPTIONS = [ALL_CHANNELS, DEMAND_GEN, DEMAND_CAP];
export const DEFAULT_SORT_OPTIONS = [...SINGLE_COLOR_SORT_OPTIONS, DEMAND_GEN_VS_CAP];
const ACTUAL_VALUES = "Actual Values";
const FULL_BREAKDOWN = "100%";
const COMBINED_DEMAND_GEN_NAME = "All Combined Demand Generation";
const COMBINED_DEMAND_CAP_NAME = "All Combined Capture Generation";
const GROUP_BY_SUPPORT_DATE_STR = "2025-03-21T00:00:00";

export const SpendAndEffectModal: React.FC<SpendAndEffectModalProps> = ({
  onClose,
  data,
  valueFormatter,
  sortOptions,
  kpiType,
  excelDownload,
  groupBy,
  setGroupBy,
  groupByOptions,
  sortDropdownValue,
  setSortDropdownValue,
  modelDate,
}) => {
  const [displayActualValues, setDisplayActualValues] = useState<boolean>(false);
  const [activeChannel, setActiveChannel] = useState<string>(ALL_CHANNELS);
  const [barData, setBarData] = useState<any[]>(spendAndEffectDataSort(data, sortDropdownValue));
  const [xAxisScale, setXAxisScale] = useState<number>();
  const [selectedBreakdown, setSelectedBreakdown] = useState<string>(FULL_BREAKDOWN);
  const [showGlossary, setShowGlossary] = useState(false);

  const groupByDisabled = useMemo(() => {
    return lessThanIgnoreTime(modelDate, GROUP_BY_SUPPORT_DATE_STR);
  }, [modelDate]);

  useEffect(() => {
    setBarData(spendAndEffectDataSort(data, sortDropdownValue));
  }, [data, sortDropdownValue]);

  const activeChannelData = useMemo(() => {
    return DEFAULT_SORT_OPTIONS.includes(activeChannel)
      ? {}
      : data.find(item => item.name === activeChannel);
  }, [data, activeChannel]);

  const isValueAdded = activeChannelData.added || false;

  const activeChannelMap = useMemo(
    () =>
      data.reduce(
        (acc, channel) => {
          acc[ALL_CHANNELS].add(channel.name);
          acc[DEMAND_GEN_VS_CAP].add(channel.name);
          if (channel.added) {
            acc[DEMAND_GEN].add(channel.name);
          } else {
            acc[DEMAND_CAP].add(channel.name);
          }
          acc[channel.name] = channel.contributors;
          return acc;
        },
        {
          [ALL_CHANNELS]: new Set(),
          [DEMAND_GEN]: new Set(),
          [DEMAND_CAP]: new Set(),
          [DEMAND_GEN_VS_CAP]: new Set(),
        }
      ),
    [data]
  );

  const demandGenBarData = useMemo(
    () => getBarChartContributorsData(barData, activeChannelMap[DEMAND_GEN]),
    [activeChannelMap, barData]
  );

  const demandCapBarData = useMemo(
    () => getBarChartContributorsData(barData, activeChannelMap[DEMAND_CAP]),
    [activeChannelMap, barData]
  );

  const buildCombinedBarData = (source: any[], name: string, added: boolean) => {
    let combinedBarData: any = {
      // Default "empty" values
      channelName: null,
      platformName: null,
      tacticName: null,
      brandNonBrandName: null,
      incrementality: false,
      contributors: new Set() as Set<string>,
      contributorsSpendSum: 0,
      contributorsEffectSum: 0,
      totalSpendForChannel: 0,
      totalEffectForChannel: 0,
      value: 0,
      value2: 0,
      label: "",
      label2: {
        top: "",
        bottom: "",
      },
      spendOverOutcome: 0,
      spendRawValue: 0,
      effectRawValue: 0,
      contributorsSpendSumRawValue: 0,
      contributorsEffectSumRawValue: 0,
      totalActualizedSpend: 0,
      totalActualizedSpendRawValue: 0,
      totalActualizedEffect: 0,
      totalActualizedEffectRawValue: 0,
      // Input values
      name,
      displayName: name,
      added: added,
    };

    source.forEach(row => {
      Object.keys(row).forEach(key => {
        if (
          [
            "added",
            "brandNonBrandName",
            "channelName",
            "displayName",
            "incrementality",
            "label",
            "label2",
            "name",
            "platformName",
            "tacticName",
            "totalActualizedEffectRawValueLabel",
            "totalActualizedSpendRawValueLabel",
            "totalEffectForChannelLabel",
            "totalSpendForChannelLabel",
          ].includes(key)
        ) {
          // Do nothing
          return;
        }

        // Combine contributor sets
        if (key === "contributors") {
          combinedBarData.contributors = new Set([
            ...Array.from(row.contributors),
            ...Array.from(combinedBarData.contributors),
          ]) as Set<string>;
          return;
        }

        // Sum any numeric key
        combinedBarData[key] = (combinedBarData[key] ?? 0) + row[key];
      });
    });

    combinedBarData = {
      ...combinedBarData,
      totalSpendForChannelLabel: currencyFormatter.format(combinedBarData.totalSpendForChannel),
      totalEffectForChannelLabel: currencyFormatter.format(combinedBarData.totalEffectForChannel),
      totalActualizedSpendRawValueLabel: currencyFormatter.format(
        combinedBarData.totalActualizedSpendRawValue
      ),
      totalActualizedEffectRawValueLabel: currencyFormatter.format(
        combinedBarData.totalActualizedEffectRawValue
      ),
    };

    return [combinedBarData];
  };

  const combinedDemandGenBarData = useMemo(() => {
    return buildCombinedBarData(demandGenBarData, COMBINED_DEMAND_GEN_NAME, true);
  }, [demandGenBarData]);

  const combinedDemandCapBarData = useMemo(() => {
    return buildCombinedBarData(demandCapBarData, COMBINED_DEMAND_CAP_NAME, false);
  }, [demandCapBarData]);

  const [sankeyPrimaryPrettyLabelSpend, sankeyPrimaryPrettyLabelEffect] = useMemo(() => {
    const keyWord = isValueAdded ? "Actualized" : "Direct";
    if (selectedBreakdown !== ACTUAL_VALUES) {
      return [`${selectedBreakdown} ${keyWord} Spend`, `${selectedBreakdown} ${keyWord} Effect`];
    }
    const spend = isValueAdded
      ? activeChannelData.totalActualizedSpendRawValueLabel
      : activeChannelData.totalSpendForChannelLabel;
    const effect = isValueAdded
      ? activeChannelData.totalActualizedEffectRawValueLabel
      : activeChannelData.totalEffectForChannelLabel;
    return [`${spend} ${keyWord} Spend`, `${effect} ${keyWord} Effect`];
  }, [isValueAdded, selectedBreakdown, activeChannelData]);

  const [spendSankeyData, effectSankeyData] = useMemo(() => {
    const activeChannelSpendTotal =
      activeChannelData.contributorsSpendSum + activeChannelData.value;
    const activeChannelEffectTotal =
      activeChannelData.contributorsEffectSum + activeChannelData.value2;

    const startingLink = isValueAdded
      ? { source: 1, target: 0, value: 0 }
      : { source: 0, target: 1, value: 0 };
    const initialSpendObj = {
      nodes: [{ name: "activeChannelSpend" }, { name: "activeChannelSpend2" }],
      links: [{ ...startingLink }],
      zeroPrimaryValue: false,
    };
    const initialEffectObj = {
      nodes: [{ name: "activeChannelEffect" }, { name: "activeChannelEffect2" }],
      links: [{ ...startingLink }],
      zeroPrimaryValue: false,
    };
    if (DEFAULT_SORT_OPTIONS.includes(activeChannel)) {
      return [initialSpendObj, initialEffectObj];
    }

    return data.reduce(
      ([spend, effect], cv) => {
        if (activeChannel === cv.name) {
          if (selectedBreakdown === ACTUAL_VALUES) {
            if (cv.spendRawValue) {
              spend.links[0].value = cv.spendRawValue;
            } else {
              spend.zeroPrimaryValue = true;
              spend.links.shift();
            }
            if (cv.effectRawValue) {
              effect.links[0].value = cv.effectRawValue;
            } else {
              effect.zeroPrimaryValue = true;
              effect.links.shift();
            }
          } else {
            if (cv.value) {
              spend.links[0].value = (cv.value / activeChannelSpendTotal) * 100;
            } else {
              spend.zeroPrimaryValue = true;
              spend.links.shift();
            }
            if (cv.value2) {
              effect.links[0].value = (cv.value2 / activeChannelEffectTotal) * 100;
            } else {
              effect.zeroPrimaryValue = true;
              effect.links.shift();
            }
          }
        }
        if (activeChannelMap[activeChannel].has(cv.name)) {
          if (selectedBreakdown === ACTUAL_VALUES) {
            if (cv[`${activeChannel}:SpendRawValue`]) {
              spend.nodes.push({ name: cv.name });
              spend.links.push({
                target: isValueAdded ? 0 : spend.nodes.length - 1,
                source: isValueAdded ? spend.nodes.length - 1 : 0,
                value: cv[`${activeChannel}:SpendRawValue`],
              });
            }

            if (cv[`${activeChannel}:EffectRawValue`]) {
              effect.nodes.push({ name: cv.name });
              effect.links.push({
                target: isValueAdded ? 0 : effect.nodes.length - 1,
                source: isValueAdded ? effect.nodes.length - 1 : 0,
                value: cv[`${activeChannel}:EffectRawValue`],
              });
            }
          } else {
            if (cv[`${activeChannel}:Spend`]) {
              spend.nodes.push({ name: cv.name });
              spend.links.push({
                target: isValueAdded ? 0 : spend.nodes.length - 1,
                source: isValueAdded ? spend.nodes.length - 1 : 0,
                value: (cv[`${activeChannel}:Spend`] / activeChannelSpendTotal) * 100,
              });
            }
            if (cv[`${activeChannel}:Effect`]) {
              effect.nodes.push({ name: cv.name });
              effect.links.push({
                target: isValueAdded ? 0 : effect.nodes.length - 1,
                source: isValueAdded ? effect.nodes.length - 1 : 0,
                value: (cv[`${activeChannel}:Effect`] / activeChannelEffectTotal) * 100,
              });
            }
          }
        }
        return [spend, effect];
      },
      [initialSpendObj, initialEffectObj]
    );
  }, [data, activeChannel, activeChannelMap, isValueAdded, activeChannelData, selectedBreakdown]);

  const colorMap = useMemo(() => {
    if (data) {
      return {
        ...colors(data),
        [COMBINED_DEMAND_GEN_NAME]: Neutral400,
        [COMBINED_DEMAND_CAP_NAME]: Neutral300,
      };
    }
  }, [data]);

  const CustomLink = useCallback(
    (
      { sourceX, targetX, sourceY, targetY, sourceControlX, targetControlX, linkWidth, payload },
      name,
      breakdown
    ) => {
      const isPrimary = isValueAdded
        ? PRIMARY_SANKEY_SET.has(payload.source.name)
        : PRIMARY_SANKEY_SET.has(payload.target.name);
      const resolvedFill = isPrimary
        ? colorMap[activeChannel]
        : colorMap[isValueAdded ? payload.source.name : payload.target.name];
      const resolvePathd = `M${sourceX},${sourceY} C${sourceControlX},${sourceY} ${targetControlX},${targetY} ${targetX},${targetY}`;
      const ratio = isValueAdded ? payload.dy / payload.target.dy : payload.dy / payload.source.dy;
      const prettyLabel =
        breakdown === ACTUAL_VALUES
          ? name === "Effect" && kpiType !== "revenue"
            ? numberFormatter.format(payload.value)
            : currencyFormatter.format(payload.value)
          : breakdown === "$1.00"
          ? formatMoney(ratio)
          : `${(ratio * 100).toFixed(2)}%`;

      return (
        <g>
          <path
            d={resolvePathd}
            fill="none"
            stroke={resolvedFill}
            strokeOpacity={0.4}
            strokeWidth={linkWidth}
            strokeLinecap="butt"
          />
          <foreignObject
            className={`customLinkForeignObject ${isValueAdded ? "valueAdded" : "valueReduced"}`}
            x={isValueAdded ? sourceX - 275 : sourceX + 165}
            y={isValueAdded ? sourceY - linkWidth / 2 : targetY - linkWidth / 2}
            height={linkWidth}
          >
            <TooltipOverlay
              tooltipChildren={
                isPrimary
                  ? name === "Spend"
                    ? SPEND_AND_EFFECT_GLOSSARY_DEFINITIONS["Direct Spend"]
                    : SPEND_AND_EFFECT_GLOSSARY_DEFINITIONS["Direct Effect"]
                  : undefined
              }
              design="primary"
              multiline
            >
              <div
                className={`sankeyLabelContainer ${isValueAdded ? "valueAdded" : "valueReduced"}`}
              >
                <div
                  className={`sankeyLabel ${isPrimary ? "primary" : ""} ${
                    isValueAdded ? "valueAdded" : "valueReduced"
                  }`}
                >
                  {isPrimary
                    ? `${isValueAdded ? "Direct" : "Actualized"} ${name}: `
                    : `${isValueAdded ? payload.source.name : payload.target.name}: `}
                  {prettyLabel}
                  &nbsp;
                </div>
              </div>
            </TooltipOverlay>
          </foreignObject>
        </g>
      );
    },
    [activeChannel, colorMap, isValueAdded, kpiType]
  );

  const CustomNode = useCallback(
    ({ width, height, payload, x, y }): React.ReactElement => {
      const shading = isValueAdded
        ? `url(#shadedStripeNeg${colorMap[payload.name]})`
        : `url(#shadedStripe${colorMap[payload.name]})`;
      const resolvedFill = PRIMARY_SANKEY_SET.has(payload.name) ? colorMap[activeChannel] : shading;
      const outlineNode = isValueAdded
        ? payload.name === "activeChannelEffect" || payload.name === "activeChannelSpend"
        : payload.name === "activeChannelEffect2" || payload.name === "activeChannelSpend2";
      return (
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          fill={resolvedFill}
          stroke={outlineNode ? "#4e4e50" : undefined}
          strokeWidth={2}
          ry={outlineNode ? 2 : undefined}
        />
      );
    },
    [colorMap, activeChannel, isValueAdded]
  );

  enum DrodownSection {
    DEFAULTS = "defaults",
    CHANNELS = "channels",
  }

  const channelOptions = useMemo(() => {
    return [
      ...DEFAULT_SORT_OPTIONS.map(option => ({ value: option, section: DrodownSection.DEFAULTS })),
      ...data.map(channel => ({ value: channel.name, section: DrodownSection.CHANNELS })),
    ];
  }, [DrodownSection.CHANNELS, DrodownSection.DEFAULTS, data]);

  const returnToAllChannelsView = useCallback(() => {
    setBarData(
      spendAndEffectDataSort(
        getBarChartContributorsData(data, activeChannelMap[ALL_CHANNELS]),
        sortDropdownValue
      )
    );
    setActiveChannel(ALL_CHANNELS);
    setSelectedBreakdown(FULL_BREAKDOWN);
  }, [activeChannelMap, data, sortDropdownValue]);

  const topBarData = useMemo(() => {
    switch (activeChannel) {
      case ALL_CHANNELS:
        return demandGenBarData;
      case DEMAND_GEN_VS_CAP:
        return combinedDemandGenBarData;
      default:
        return data.filter(el => el.name === activeChannel);
    }
  }, [activeChannel, combinedDemandGenBarData, data, demandGenBarData]);

  const bottomBarData = useMemo(() => {
    switch (activeChannel) {
      case ALL_CHANNELS:
        return demandCapBarData;
      case DEMAND_GEN_VS_CAP:
        return combinedDemandCapBarData;
      default:
        return barData;
    }
  }, [activeChannel, barData, combinedDemandCapBarData, demandCapBarData]);

  return (
    <div>
      <SpendAndEffectGlossary show={showGlossary} setShow={setShowGlossary} />
      <Modal show onHide={onClose} size="xl" className="spendAndEffectModal">
        <WidgetContainer
          header={<div>Spend and Effect Share Details</div>}
          rightActions={
            <Modal.Header closeButton className="spendEffectModalHeader">
              <ToggleSwitch
                designType={ToggleSwitchType.FILLED}
                design="primary"
                size="sm"
                label={ACTUAL_VALUES}
                checked={displayActualValues}
                onChange={setDisplayActualValues}
              />
              <Dropdown
                type={DropdownToggleType.FILLED}
                design="primary"
                size="sm"
                value={sortDropdownValue}
                onChange={value => {
                  setBarData(d => spendAndEffectDataSort(d, value));
                  setSortDropdownValue(value);
                }}
                options={sortOptions}
              />
              <TooltipOverlay
                tooltipChildren={
                  groupByDisabled
                    ? "Models created before March 21st 2025 cannot support group by functionality"
                    : undefined
                }
                design="primary"
                placement={OverlayTrigger.PLACEMENTS.BOTTOM.CENTER}
              >
                <div>
                  <Dropdown
                    design="primary"
                    className="mmmGroupByDropdown"
                    label="Group by"
                    size="sm"
                    onChange={setGroupBy}
                    options={groupByOptions}
                    value={groupBy}
                    disabled={groupByDisabled}
                  />
                </div>
              </TooltipOverlay>
              <Button
                type={ButtonType.FILLED}
                size="sm"
                icon={<MdOutlineMenuBook />}
                variant={ButtonFrameworkVariant.TRAILING_ICON}
                onClick={() => setShowGlossary(true)}
              >
                Glossary
              </Button>
              <DownloadDropdown size="sm" onClickOptions={[excelDownload]} menuOptions={["XLSX"]} />
            </Modal.Header>
          }
          bottom={
            <div className="spendAndEffectModalBottomWrapper">
              <MdOutlineWarningAmber />
              <div>
                Channels marked with an alert icon lack incrementality testing, and results should
                be interpreted cautiously.
              </div>
            </div>
          }
        >
          <Modal.Body className="spendEffectModalBody">
            <ChartContainer
              title={
                <>
                  <Dropdown
                    type={DropdownToggleType.WIDGET_TITLE}
                    value={activeChannel}
                    options={channelOptions}
                    section={[
                      { title: DrodownSection.DEFAULTS, showSectionTitle: false },
                      { title: DrodownSection.CHANNELS, showSectionTitle: false },
                    ]}
                    onChange={option => {
                      setBarData(getBarChartContributorsData(data, activeChannelMap[option]));
                      setActiveChannel(option);
                    }}
                  />
                  {!DEFAULT_SORT_OPTIONS.includes(activeChannel) && (
                    <Button
                      type={ButtonType.OUTLINED}
                      variant={ButtonFrameworkVariant.LEADING_ICON}
                      size="sm"
                      design="secondary"
                      icon={<TiArrowBack color={Brand60} />}
                      onClick={returnToAllChannelsView}
                    >
                      Back
                    </Button>
                  )}
                </>
              }
              rightActions={
                <div className="groupRightActions">
                  <VisualLegendHorizontal />
                </div>
              }
            >
              <div className="multiBarChartContainer">
                <div className="patternDefinitions">
                  <ShadedPattern color={"grey"} visibleTag={false} />
                  <ShadedPattern
                    color="white"
                    backgroundColor={"grey"}
                    id={"Neggrey"}
                    visibleTag={false}
                  />
                  {data.map(item => (
                    <div key={`modalShadedPatternDefs${item.name}`}>
                      <ShadedPattern color={colorMap[item.name]} visibleTag={false} />
                      <ShadedPattern
                        color="white"
                        backgroundColor={colorMap[item.name]}
                        id={`Neg${colorMap[item.name]}`}
                        visibleTag={false}
                      />
                    </div>
                  ))}
                </div>
                <div
                  className={cn("SpendAndEffectBarChartHeader", {
                    hideActualValues: !displayActualValues,
                    showActualValues: displayActualValues,
                  })}
                >
                  <TooltipOverlay
                    tooltipChildren={SPEND_AND_EFFECT_GLOSSARY_DEFINITIONS.Actualized}
                    design="primary"
                    multiline
                    placement={OverlayTrigger.PLACEMENTS.BOTTOM.RIGHT}
                  >
                    <div className="label">Actualized</div>
                  </TooltipOverlay>
                  <div className="verticalDivider" />
                  <TooltipOverlay
                    tooltipChildren={SPEND_AND_EFFECT_GLOSSARY_DEFINITIONS.Direct}
                    design="primary"
                    multiline
                    placement={OverlayTrigger.PLACEMENTS.BOTTOM.LEFT}
                  >
                    <div className="label">Direct</div>
                  </TooltipOverlay>
                </div>
                {![DEMAND_CAP, DEMAND_GEN].includes(activeChannel) && (
                  <>
                    <SpendAndEffectBarChart
                      expandedModal
                      activeChannel={activeChannel}
                      setActiveChannel={setActiveChannel}
                      barData={topBarData}
                      setBarData={setBarData}
                      data={data}
                      displayActualValues={displayActualValues}
                      valueFormatter={valueFormatter}
                      className="activeChannel"
                      colorMap={colorMap}
                      barSize={24}
                      key={"SpendandEffectMMMModalActiveChannel"}
                      xAxisScale={xAxisScale}
                      setXAxisScale={setXAxisScale}
                      groupBy={groupBy}
                    />
                    <div className="horizontalDivider" />
                  </>
                )}
                <SpendAndEffectBarChart
                  expandedModal
                  activeChannel={activeChannel}
                  setActiveChannel={setActiveChannel}
                  barData={bottomBarData}
                  setBarData={setBarData}
                  data={data}
                  displayActualValues={displayActualValues}
                  valueFormatter={valueFormatter}
                  colorMap={colorMap}
                  barSize={24}
                  key={"SpendandEffectMMMModalAllChannels"}
                  xAxisScale={xAxisScale}
                  setXAxisScale={setXAxisScale}
                  groupBy={groupBy}
                />
                <div
                  className="multiBarChartContainerBackground"
                  onClick={
                    DEFAULT_SORT_OPTIONS.includes(activeChannel)
                      ? undefined
                      : returnToAllChannelsView
                  }
                />
              </div>
              <div
                className={cn("secondaryChart", {
                  breakdownAvailable: !DEFAULT_SORT_OPTIONS.includes(activeChannel),
                })}
              >
                <ChartContainer
                  title={
                    !DEFAULT_SORT_OPTIONS.includes(activeChannel) ? (
                      <div className="titleWithDropdown">
                        <span>Breakdown by </span>
                        <Dropdown
                          options={[FULL_BREAKDOWN, "$1.00", ACTUAL_VALUES]}
                          value={selectedBreakdown}
                          onChange={setSelectedBreakdown}
                          type={DropdownToggleType.EMPTY}
                        />
                      </div>
                    ) : activeChannel === DEMAND_GEN_VS_CAP ? (
                      "Select a single channel from the dropdown to see channel interactions"
                    ) : (
                      "Select a channel to see breakdown"
                    )
                  }
                >
                  {!DEFAULT_SORT_OPTIONS.includes(activeChannel) && (
                    <div className="sankeyContainer">
                      <div className="singleSankeyContainer">
                        {spendSankeyData.links.length ? (
                          <>
                            <div>Spend: </div>
                            <TooltipOverlay
                              tooltipChildren={
                                SPEND_AND_EFFECT_GLOSSARY_DEFINITIONS["Actualized Spend"]
                              }
                              design="primary"
                              multiline
                            >
                              <div className={`totalSankeyLabel ${isValueAdded ? "added" : ""}`}>
                                <div className="totalSankeyLabelInner">
                                  {sankeyPrimaryPrettyLabelSpend}
                                </div>
                                {
                                  <div className="zeroPrimaryValue">
                                    {spendSankeyData.zeroPrimaryValue
                                      ? isValueAdded
                                        ? "No direct spend"
                                        : "Actualized Spend reduced to zero"
                                      : ""}
                                  </div>
                                }
                              </div>
                            </TooltipOverlay>
                            <div className={`sankeyChart ${isValueAdded ? "added" : ""}`}>
                              <Sankey
                                height={spendSankeyData.links.length > 5 ? 280 : 180}
                                width={170}
                                data={spendSankeyData}
                                node={CustomNode}
                                link={props => CustomLink(props, "Spend", selectedBreakdown)}
                                nodePadding={19}
                              ></Sankey>
                            </div>
                          </>
                        ) : (
                          <div className="zeroDataMessage">Zero spend for this channel</div>
                        )}
                      </div>
                      <div className="singleSankeyContainer">
                        {effectSankeyData.links.length ? (
                          <>
                            <div className="effectTitle">Effect: </div>
                            <TooltipOverlay
                              tooltipChildren={
                                SPEND_AND_EFFECT_GLOSSARY_DEFINITIONS["Actualized Effect"]
                              }
                              design="primary"
                              multiline
                            >
                              <div className={`totalSankeyLabel ${isValueAdded ? "added" : ""}`}>
                                <div className="totalSankeyLabelInner">
                                  {sankeyPrimaryPrettyLabelEffect}
                                </div>
                                {
                                  <div className="zeroPrimaryValue">
                                    {effectSankeyData.zeroPrimaryValue
                                      ? isValueAdded
                                        ? "No direct effect"
                                        : "Actualized Effect reduced to zero"
                                      : ""}
                                  </div>
                                }
                              </div>
                            </TooltipOverlay>
                            <div className={`sankeyChart effect ${isValueAdded ? "added" : ""}`}>
                              <Sankey
                                height={effectSankeyData.links.length > 5 ? 280 : 180}
                                width={170}
                                data={effectSankeyData}
                                node={CustomNode}
                                link={props => CustomLink(props, "Effect", selectedBreakdown)}
                                nodePadding={19}
                              ></Sankey>
                            </div>
                          </>
                        ) : (
                          <div className="zeroDataMessage">Zero effect for this channel</div>
                        )}
                      </div>
                    </div>
                  )}
                </ChartContainer>
              </div>
            </ChartContainer>
          </Modal.Body>
        </WidgetContainer>
      </Modal>
    </div>
  );
};
